import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import type { PlotDetail } from "@/api/types";
import { useIsBackdrop } from "@/app/layout/backdrop";
import { useAuthStore } from "@/stores/authStore";

/**
 * 플롯 본문의 {{user}} 는 서버가 요청자의 대표 프로필 이름(없으면 "당신")으로 치환한다(AC0-250). 응답이 보는 사람에 따라 다르므로
 * 캐시 키에 그 이름을 넣는다 — 로그인·로그아웃·프로필 이름 변경 때 다시 받는다. 비회원·프로필 없음은 "" (AC0-359)
 */
export const usePlotViewer = () => useAuthStore((s) => s.profile?.name ?? "");

/** GET /api/plots/:key (key = slug 또는 예전 UUID — 서버가 둘 다 받는다). 비회원·회원 공용(로그인돼 있으면 토큰을 보내 이름 치환). 플롯 프로필·비회원 채팅방이 같은 캐시를 본다. */
export function usePlot(key: string | undefined) {
  const viewer = usePlotViewer();
  return useQuery({
    queryKey: qk.plot(key ?? "", viewer),
    queryFn: () => apiFetch<PlotDetail>(ep.plot(key as string)),
    enabled: Boolean(key),
    staleTime: 10 * 60 * 1000, // providers 기본과 같은 10분을 명시 — slug 주소로 바꿀 때 미리 넣은 캐시를 다시 요청하지 않게(useCanonicalPlotPath)
  });
}

/**
 * 외부 주소는 slug 하나 (AC0-161: UUID 는 DB 안에서만). 예전 UUID 주소로 들어오면 로드된 플롯의 slug 주소로 바꾼다(replace).
 * slug 키에 같은 데이터를 먼저 넣어 두어 주소가 바뀌어도 다시 요청하지 않는다. 주소는 요청 값이 아니라 응답(DB) 값으로 만든다
 */
export function useCanonicalPlotPath(key: string, plot: UseQueryResult<PlotDetail>, prefix: "/plots" | "/chat") {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const backdrop = useIsBackdrop();
  const viewer = usePlotViewer();
  const data = plot.data;
  useEffect(() => {
    if (!data || data.slug === key || backdrop) return;
    qc.setQueryData(qk.plot(data.slug, viewer), data);
    navigate(`${prefix}/${encodeURIComponent(data.slug)}`, { replace: true });
  }, [data, key, prefix, qc, navigate, backdrop, viewer]);
}
